// Centralized aspect-ratio definitions for all static ad-image surfaces.
//
// This is the SINGLE source of truth for which aspect ratios the ad-image
// tools support (Instant Ad Generation, Ads Express, Ads Express KC,
// Creative Tinder, and Creative Tinder "Review & create sets"). Both the
// client pickers and the server render paths import from here so the list,
// labels, and engine-routing can never drift.
//
// Engine routing rule (see `gptImageSizeFor`):
//   - On-grid ratios (1:1, 9:16, 16:9) have an EXACT GPT-Image preset, so
//     they may use the GPT-Image (CT) pipeline.
//   - Off-grid ratios (4:5, 2:3) have NO exact GPT-Image preset — GPT-Image
//     would silently approximate them (e.g. 4:5 -> 3:4). These MUST route to
//     nano-banana, which accepts the exact `aspect_ratio` string.

export type AspectRatio = "1:1" | "4:5" | "9:16" | "16:9" | "2:3";

export interface AspectRatioDimensions {
  width: number;
  height: number;
}

export interface AspectRatioOption {
  value: AspectRatio;
  /** Short ratio label, e.g. "4:5". */
  label: string;
  /** Exact rendered pixel width for this ratio. */
  width: number;
  /** Exact rendered pixel height for this ratio. */
  height: number;
  /** Pixel dimensions for display, e.g. "1080 × 1350". Derived from width/height. */
  dimensions: string;
}

// The SINGLE source of truth for the exact pixel dimensions every ratio
// renders/saves at. The display label (`dimensions`) and the actual image
// normalization (see `dimensionsFor`) both derive from this map so the
// advertised size and the real output can never drift.
const RATIO_DIMENSIONS: Record<AspectRatio, AspectRatioDimensions> = {
  "1:1": { width: 1080, height: 1080 },
  "4:5": { width: 1080, height: 1350 },
  "9:16": { width: 1080, height: 1920 },
  "16:9": { width: 1920, height: 1080 },
  "2:3": { width: 1080, height: 1620 },
};

/** Exact target pixel dimensions for a ratio (defaults to the default ratio for unknown input). */
export function dimensionsFor(ratio: string): AspectRatioDimensions {
  return RATIO_DIMENSIONS[normalizeAspectRatio(ratio)];
}

function formatDimensions({ width, height }: AspectRatioDimensions): string {
  return `${width} × ${height}`;
}

// Ordered list used to render every picker. Built from RATIO_DIMENSIONS so
// the displayed dimensions always match what the renderer normalizes to.
export const AD_ASPECT_RATIO_OPTIONS: readonly AspectRatioOption[] = (
  ["1:1", "4:5", "9:16", "16:9", "2:3"] as AspectRatio[]
).map((value) => {
  const dims = RATIO_DIMENSIONS[value];
  return {
    value,
    label: value,
    width: dims.width,
    height: dims.height,
    dimensions: formatDimensions(dims),
  };
});

export const AD_ASPECT_RATIOS: readonly AspectRatio[] =
  AD_ASPECT_RATIO_OPTIONS.map((o) => o.value);

export const DEFAULT_AD_ASPECT_RATIO: AspectRatio = "4:5";

// localStorage keys for "remember my last selection" across visits.
export const AD_ASPECT_RATIO_STORAGE_KEY = "personaos.adAspectRatio";
export const AD_ASPECT_RATIOS_STORAGE_KEY = "personaos.adAspectRatios";

// GPT-Image (Fal `image_size`) preset per ratio. `null` means there is no
// exact preset and the render MUST go to nano-banana with the exact
// aspect_ratio string instead.
const GPT_IMAGE_SIZE: Record<AspectRatio, string | null> = {
  "1:1": "square_hd",
  "4:5": null,
  "9:16": "portrait_16_9",
  "16:9": "landscape_16_9",
  "2:3": null,
};

/** Returns the exact GPT-Image `image_size` preset, or null for off-grid. */
export function gptImageSizeFor(ratio: string): string | null {
  return GPT_IMAGE_SIZE[ratio as AspectRatio] ?? null;
}

// ── 9:16 → 4:5 safe zone ────────────────────────────────────────────────────
// Expansion-based deliverables are ONE creative in two sizes: 4:5 and 9:16.
// Their key content (product, copy, logo, CTA,
// badges) inside a CENTERED 4:5 region so the same image works cropped to 4:5
// and full-bleed at 9:16; the top/bottom bands are background-only extension.
// Everything below derives from RATIO_DIMENSIONS so the safe-zone math can
// never drift from the real render sizes. These helpers describe expansion
// geometry, NOT template admission: standard Ads Express first freezes its
// template plan and only expands a verified 4:5 reference requested at 9:16.
export const SAFE_ZONE_SOURCE_RATIO: AspectRatio = "9:16";
export const SAFE_ZONE_TARGET_RATIO: AspectRatio = "4:5";

// Platform UI geometry is independent of the existing centered expansion bands.
export const PLATFORM_SAFE_RECT_916 = { top: 270, bottom: 1535, left: 0, right: 1080 } as const;
export const DIRECT_916_PLATFORM_SAFE_ZONE_DIRECTIVE = "9:16 PLATFORM SAFE ZONE, STRICT: this ad is 1080x1920 and will run in Instagram and Facebook Reels and Stories, where app UI covers the top 270px and bottom 385px of the frame. Place ALL text (headline, body, badges, price), the logo, the CTA button, and the key details of the product ONLY in the middle area between y=270 and y=1535 (14% to 80% of the frame height). The top 14% and bottom 20% of the canvas must contain only background, color, texture or scenery. Extend the existing background fully to all edges (full bleed) and put no text, logo or CTA in those zones. Keep the template's layout, hierarchy and styling. Fit them into the middle area by tightening spacing and scaling elements down, not by dropping elements. Do not add new elements.";

const SZ_SRC = RATIO_DIMENSIONS[SAFE_ZONE_SOURCE_RATIO]; // 1080×1920
const SZ_TGT = RATIO_DIMENSIONS[SAFE_ZONE_TARGET_RATIO]; // 1080×1350

// Height of the centered safe region on the 9:16 canvas: the 4:5 crop shares
// the same width (1080), so the safe height is the 4:5 height and each
// background-only band is half the leftover height (285px, ~14.84%).
export const SAFE_ZONE_BAND_PX = Math.round((SZ_SRC.height - SZ_TGT.height) / 2);
export const SAFE_ZONE_BAND_FRACTION = SAFE_ZONE_BAND_PX / SZ_SRC.height;

/** True when a render at this ratio must respect the centered 4:5 safe zone. */
export function safeZoneApplies(ratio: string): boolean {
  return ratio === SAFE_ZONE_SOURCE_RATIO;
}

/**
 * The ratio the BASE creative actually renders at. A 9:16 deliverable is
 * mechanically guaranteed: the creative renders at 4:5 through the normal
 * 4:5 pipeline (identical to the standalone 4:5 sibling of the same ad),
 * then the server extends it to 9:16 with background-only bands (see
 * server/services/safe-zone-extender.ts). Every other ratio renders as-is
 * (byte-for-byte unchanged params). Prompt-directive enforcement was tried
 * first and failed — image models just fill the whole 9:16 canvas.
 */
export function safeZoneBaseRenderRatio(ratio: string): string {
  return safeZoneApplies(ratio) ? SAFE_ZONE_TARGET_RATIO : ratio;
}

// Feather width (px) used when the ORIGINAL 4:5 pixels are composited back
// over the AI-extended 9:16 canvas: the outer edges of the pasted region get
// an alpha ramp so tiny reproduction drift in the outpainted bands can't show
// as a hard seam at y=285 / y=1635.
export const SAFE_ZONE_FEATHER_PX = 32;

// Prompt for the Bria Expand band-extension pass (fal-ai/bria/expand). The
// model receives the ORIGINAL 4:5 creative plus canvas-placement geometry and
// generates only the new bands — it never re-renders the ad itself, which is
// what made general edit models (nano-banana-2/edit) echo ghost headline text
// into the bands. All numbers derive from RATIO_DIMENSIONS above; exported as
// a constant so tests can assert on it.
export const SAFE_ZONE_EXTENSION_PROMPT =
  `This ${SZ_TGT.width}x${SZ_TGT.height} (4:5) image is a FINISHED advertisement being extended onto a ${SZ_SRC.width}x${SZ_SRC.height} (9:16) canvas. ` +
  `Keep the advertisement EXACTLY as-is — do not crop, move, rescale, restyle, or alter it in any way. ` +
  `Fill ONLY the new top ${SAFE_ZONE_BAND_PX}px and bottom ${SAFE_ZONE_BAND_PX}px bands with a natural, seamless continuation of the advertisement's own background scene: extended scenery, surfaces, texture, gradient, lighting, or ambient environmental elements flowing out of the ad. ` +
  `Absolutely no words, no letters, no logos, no products, no people, and no graphic elements in the top/bottom bands — background only.`;

// Task #1368 — negative prompt for the same Bria Expand pass, sent ONLY when
// the rendering user holds the `safe_zone_enhanced_render` Beta Feature grant
// (Task #1331, per user per brand) for the brand being rendered. No grant =
// the submit payload has no negative_prompt key at all, byte-for-byte.
export const SAFE_ZONE_EXTENSION_NEGATIVE_PROMPT =
  "text, letters, words, numbers, typography, gibberish text, pseudo-text, logos, watermarks, wordmarks, people, faces, hands, product duplicates, duplicated or mirrored content, extra copies of the advertisement, seams, hard edges, blur smears, artifacts, distortion, low quality";

// Task #1858 — QA re-roll ESCALATION (enhanced arm only). An identical
// re-roll of an edge-flush creative usually reproduces the same flagged
// bands, so re-roll attempts vary the expand pass instead of repeating it:
//   level 1 (2nd attempt): the negative prompt leans harder on the exact
//     defects the band QA flags (ghosted headlines, CTA pills/buttons,
//     fine print rows).
//   level 2 (3rd+ attempt): additionally, the positive prompt gains a
//     strict "plain background only" suffix that pushes the model toward
//     minimal, structure-free continuation.
// The baseline (opt-out) arm NEVER sees these — its retry payload is
// byte-identical to its first attempt.
export const SAFE_ZONE_EXTENSION_ESCALATED_NEGATIVE_PROMPT =
  SAFE_ZONE_EXTENSION_NEGATIVE_PROMPT +
  ", ghosted duplicate of the advertisement's headline, duplicated headline text, call-to-action button, CTA pill, button shape, pill shape, banner, ribbon, badge, price tag, fine print, rows of small text, legal text";

export const SAFE_ZONE_EXTENSION_STRICT_PROMPT_SUFFIX =
  " STRICT MODE: render the bands as PLAIN, quiet background only — a simple continuation of the background color, gradient, texture, or scenery immediately adjacent to the ad's top/bottom edges. Prefer minimal, almost-empty bands over detailed ones. Never continue, repeat, or echo any text, button, pill, card, badge, or graphic element from the advertisement into the bands.";

// Safe-zone EDGE COMPOSITION directive, appended to the BASE 4:5 render
// prompt of a 9:16 deliverable — but ONLY when the rendering user holds the
// same `safe_zone_enhanced_render` grant that arms the extension's negative
// prompt (no grant = base prompt byte-for-byte unchanged). Root cause it
// fixes: when the base 4:5 puts a face/inset card flush against its top or
// bottom edge, the Bria Expand pass "continues" it into the band — the
// negative prompt (people/faces) loses to strong edge continuation — and the
// result is a head split across the band seam. Keeping such elements off the
// horizontal edges of the base render is the only reliable prevention.
export const SAFE_ZONE_EDGE_COMPOSITION_DIRECTIVE =
  "EDGE COMPOSITION — STRICT: this artwork will later be extended vertically with background-only bands above and below, so its TOP and BOTTOM edges must be clean extension seams. Do NOT let any face, head, person, product, framed inset card, or text be cropped or cut off by the top or bottom edge of the canvas — keep every such element fully visible with clear breathing room (at least a 5% inner margin) from the top and bottom edges. In particular, headlines, body copy, CTA buttons/pills, prices, badges, and logos must NOT sit flush against (or touch) the top or bottom edge — leave visible background padding above the topmost element and below the bottommost element. Only background scenery, surfaces, gradients, or ambient texture may touch the top and bottom edges.";

// Ads Express brand-only, default-on cropped-head guard. Compose the 4:5
// source with its eventual 9:16 expansion in mind, rather than asking the
// extender to invent anatomy or infer a background from clothing at the edge.
export const SAFE_ZONE_PERSON_GUARD_COMPOSITION_DIRECTIVE =
  "9:16 SAFE-ZONE COMPOSITION: This finished 4:5 ad will be centered inside a taller 9:16 canvas. Reserve the outermost 8% at BOTH the top and bottom of this 4:5 image for the actual background only: continue its existing background texture, gradient, or panel colors there. Keep all people, hair, faces, body parts, products, text, logos, and buttons entirely inside the remaining central area. If the reference deliberately crops a person, preserve that crop INSIDE its layout but inset the layout from the outer edge with background around it; do not reconstruct missing anatomy. Reposition or scale the composition as needed so the background reaches both canvas edges. Do not introduce any new content in these background margins.";

export function planGuardedSafeZoneComposition(
  prompt: string,
  opts: { aspectRatio: string; isAdsExpress: boolean; personSeamGuard: boolean },
): string {
  if (!safeZoneApplies(opts.aspectRatio) || !opts.isAdsExpress || !opts.personSeamGuard) return prompt;
  return `${prompt}\n\n${SAFE_ZONE_PERSON_GUARD_COMPOSITION_DIRECTIVE}`;
}

// ── Per-brand image-engine override (Task #1777) ────────────────────────────
// Master-admin-only test override forcing ALL of a brand's Ads Express static
// ad renders onto one engine. Null/absent = today's split routing (the
// "Default"): GPT-Image-2 for on-grid ratios with an exact preset (1:1, 9:16,
// 16:9 — see GPT_IMAGE_SIZE) and nano-banana-2 for off-grid ratios (4:5, 2:3,
// including the 4:5 base that a 9:16 deliverable renders before the safe-zone
// extender outpaints it to full height).
//
// CAPABILITY MATRIX (verified against Fal's live OpenAPI schemas):
//   - openai/gpt-image-2[/edit] accepts explicit {width,height} (multiples of
//     16, max edge 3840, ≥655,360 px). A real 1088×1360 render returned a
//     1088×1360 PNG exactly.
//   - fal-ai/flux-2-pro[/edit] accepts explicit {width,height} (multiples of
//     16, 256–2560 per edge, ≤4.19MP). A real 1088×1360 render returned a
//     1088×1360 JPEG exactly.
//   - fal-ai/gpt-image-2.5-flare[/edit] accepts explicit {width,height};
//     concrete sizes require multiples of 16, max edge 3840, aspect ≤3:1,
//     and 655,360–8,294,400 total pixels. Its edit request requires
//     image_urls (max 16); both routes accept the same image_size.
// So all candidate engines cover every ratio → ONE dropdown, no disabled
// options and no on-grid/off-grid split controls. Renders happen at the
// exact-ratio multiple-of-16 sizes below, then the EXISTING normalize+persist
// step resizes to the advertised RATIO_DIMENSIONS — the same mechanism the
// default nano-banana path (which renders "near-but-inexact", e.g. 1080×1340)
// already relies on. For 9:16 deliverables, safeZoneBaseRenderRatio uses the
// 4:5 base creative before extending the background-only bands. No engine is
// ever silently substituted.
export type AdImageEngineOverride = "gpt-image-2" | "flux-2-pro";

export const AD_IMAGE_ENGINE_OVERRIDES: readonly AdImageEngineOverride[] = [
  "gpt-image-2",
  "flux-2-pro",
];

export function isAdImageEngineOverride(v: unknown): v is AdImageEngineOverride {
  return typeof v === "string" &&
    (AD_IMAGE_ENGINE_OVERRIDES as readonly string[]).includes(v);
}

// Exact-ratio render sizes for overridden engines. Every entry is the
// smallest ≥1080-class size where BOTH dimensions are multiples of 16 AND the
// ratio is mathematically exact, satisfying the verified custom-size
// constraints for GPT Image 2, GPT Image 2.5 Flare, and FLUX.2 [pro].
// (9:16 is listed for completeness but never hit: safeZoneBaseRenderRatio
// maps 9:16 renders to their 4:5 base first, override or not.)
export const OVERRIDE_ENGINE_RENDER_SIZES: Record<AspectRatio, AspectRatioDimensions> = {
  "1:1": { width: 1088, height: 1088 },
  "4:5": { width: 1088, height: 1360 },
  "2:3": { width: 1088, height: 1632 },
  "16:9": { width: 2048, height: 1152 },
  "9:16": { width: 1152, height: 2048 },
};

export function overrideEngineRenderSize(ratio: string): AspectRatioDimensions {
  return OVERRIDE_ENGINE_RENDER_SIZES[normalizeAspectRatio(ratio)];
}

export function isAspectRatio(v: unknown): v is AspectRatio {
  return typeof v === "string" && (AD_ASPECT_RATIOS as readonly string[]).includes(v);
}

export function normalizeAspectRatio(v: unknown): AspectRatio {
  return isAspectRatio(v) ? v : DEFAULT_AD_ASPECT_RATIO;
}
